import {
  BodyText,
  Button,
  Callout,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  HeaderText,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
} from "@/components/ui";
import { InfoIcon } from "lucide-react";
import * as React from "react";

interface HistoryRow {
  id?: string | number;
  date: string;
  result: string | React.ReactNode;
  resultTag?: React.ReactNode;
  infoItems?: string[];
  onVoid?: () => void;
  isVoidPending?: boolean;
}

interface HistoryModalProps {
  title: string;
  rows: HistoryRow[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage?: string;
  className?: string;
}

export const HistoryModal = ({
  title,
  rows,
  open,
  onOpenChange,
  isLoading = false,
  isError = false,
  emptyMessage = "No history found.",
}: HistoryModalProps) => {
  const hasRows = rows && rows.length > 0;

  const renderResult = (
    result: string | React.ReactNode,
    resultTag?: React.ReactNode,
  ) => {
    if (React.isValidElement(result)) {
      return result;
    }
    return (
      <BodyText size="sm" className="flex items-center gap-2">
        {result}
        {resultTag}
      </BodyText>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogOverlay className="z-20" />
      <DialogContent className="sm:max-w-[550px] max-h-[85vh] z-20 flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle asChild>
            <HeaderText size="md">History: {title}</HeaderText>
          </DialogTitle>
          <DialogDescription className="hidden">History</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50%] h-10 !p-2">
                  <BodyText size="sm" bold textColor="tertiary">
                    Date
                  </BodyText>
                </TableHead>
                <TableHead className="w-[50%] h-10 !p-2">
                  <BodyText size="sm" bold textColor="tertiary">
                    Result
                  </BodyText>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody isLoading={isLoading} isError={isError} columnCount={2}>
              {!isLoading && !isError && !hasRows ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Callout title="No Results" description={emptyMessage} />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, idx) => (
                  <TableRow key={row.id ?? idx}>
                    <TableCell className="border">
                      <BodyText size="sm">{row.date}</BodyText>
                    </TableCell>
                    <TableCell className="border">
                      <div className="flex justify-between">
                        {renderResult(row.result, row.resultTag)}
                        {(row.infoItems || row.onVoid) && (
                          <Popover>
                            <PopoverTrigger aria-label="See log">
                              <Tooltip
                                tooltipContentText="See log"
                                side="bottom"
                                contentClassName="z-30"
                              >
                                <InfoIcon className="w-4 h-4 stroke-link" />
                              </Tooltip>
                            </PopoverTrigger>
                            <PopoverContent
                              side="right"
                              className="shadow-xl flex flex-col gap-2"
                            >
                              {row.infoItems?.map((item, idx, arr) => (
                                <>
                                  <BodyText
                                    size="sm"
                                    textColor="tertiary"
                                    // biome-ignore lint/suspicious/noArrayIndexKey: list is static and small
                                    key={idx}
                                  >
                                    {item}
                                  </BodyText>
                                  {idx < arr.length - 1 && <Separator />}
                                </>
                              ))}
                              {row.onVoid && (
                                <div className="flex justify-end">
                                  <Button
                                    variant="danger"
                                    size="xs"
                                    onClick={row.onVoid}
                                    isLoading={row.isVoidPending}
                                  >
                                    Void
                                  </Button>
                                </div>
                              )}
                              <PopoverArrow fill="white" />
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export type { HistoryRow, HistoryModalProps };
